import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import './../styles/orderPageStyles.css';
import OrderHeaderCard from './OrderHeaderCard';
import OrderTicketsCard from './OrderTicketsCard';
import OrderTransfersCard from './OrderTransfersCard';
import OrderActions from './OrderActions';
import OrderNotesCard from './OrderNotesCard';
import OrderEmailsCard from './OrderEmailsCard';
import OrderCustomFieldsCard from './OrderCustomFieldsCard';
import AttributionTimelineCard from '../../shared/components/AttributionTimelineCard';
import SeatReassignmentModal from './SeatReassignmentModal';
import TicketTypeChangeModal from './TicketTypeChangeModal';
import OrderGiftCardsCard, { giftCardShape } from './OrderGiftCardsCard';
import GiftCardVoidRefundModal from './GiftCardVoidRefundModal';
import useOrderSeatReassignment from '../hooks/useOrderSeatReassignment';
import useGiftCardVoidRefund from '../hooks/useGiftCardVoidRefund';

const refundPrefillFromLink = (tickets) => {
  const params = new URLSearchParams(window.location.search);
  const ticket = (tickets || []).find((t) => String(t.id) === params.get('refund_ticket_id'));
  const amount = Number(params.get('refund_amount'));
  return ticket && amount > 0 ? { ticket, amount } : null;
};

const OrderPage = (props) => {
  const [refundPrefill, setRefundPrefill] = useState(() => (props.capabilities.canRefund && props.capabilities.canManageRefunds ? refundPrefillFromLink(props.tickets) : null));
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('refund_ticket_id')) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const {
    order, buyer, event, emails, tickets, financials, capabilities, printing, urls,
    team, currentUser, confirm, csrfToken, refundReasons,
    venueOwedBalance, canBypassRefundRestrictions,
    customFieldValues, passAnswers, defaultCountry, recipientCount, seating, giftCards, ticketTypeOptions, upgradePaymentMethods, upgradeLinks, attributionTimeline,
  } = props;

  const afterReassigned = ({ ticket, refundAmount }) => (refundAmount ? setRefundPrefill({ ticket, amount: refundAmount }) : window.location.reload());
  const canRefund = Boolean(capabilities.canRefund && capabilities.canManageRefunds);

  const seatReassignment = useOrderSeatReassignment({
    csrfToken, url: urls.seatReassignments, previewUrl: urls.seatReassignmentPreview, onReassigned: afterReassigned,
  });

  const typeChange = useOrderSeatReassignment({
    csrfToken,
    url: urls.seatReassignments,
    previewUrl: urls.seatReassignmentPreview,
    onReassigned: afterReassigned,
    toParams: (toTicketTypeId) => ({ to_ticket_type_id: toTicketTypeId }),
    successMessage: 'Ticket type changed',
    failureMessage: 'Could not change the ticket type. Please try again.',
  });

  const giftCardAction = useGiftCardVoidRefund({
    csrfToken, onDone: () => window.location.reload(),
  });

  return (
    <div className="op-page">
      <div className="op-page-inner">
        <div className="op-header-row">
          <h1 className="op-title">Transaction #{order.orderNumber}</h1>
          <OrderActions
            order={order}
            buyer={buyer}
            urls={urls}
            team={team}
            currentUser={currentUser}
            confirm={confirm}
            capabilities={capabilities}
            printing={printing}
            csrfToken={csrfToken}
            refundReasons={refundReasons}
            venueOwedBalance={venueOwedBalance}
            canBypassRefundRestrictions={canBypassRefundRestrictions}
            tickets={tickets}
            recipientCount={recipientCount}
            refundPrefill={refundPrefill}
          />
        </div>
        <OrderHeaderCard order={order} buyer={buyer} event={event} financials={financials} />
        <OrderTicketsCard
          tickets={tickets}
          canReassignSeat={Boolean(capabilities.canReassignSeat)}
          onChangeSeat={seating ? seatReassignment.openFor : null}
          onChangeType={typeChange.openFor}
        />
        {upgradeLinks.map((link) => (
          <p key={link.url} className="op-muted"><a href={link.url}>{link.label}</a></p>
        ))}
        <SeatReassignmentModal
          key={seatReassignment.ticket ? seatReassignment.ticket.id : 'closed'}
          ticket={seatReassignment.ticket}
          seating={seating}
          paymentMethods={upgradePaymentMethods}
          currency={financials && financials.currency}
          submitting={seatReassignment.submitting}
          onClose={seatReassignment.close}
          onSubmit={seatReassignment.submit}
          onPreview={seatReassignment.preview}
          canRefund={canRefund}
        />
        <TicketTypeChangeModal
          key={typeChange.ticket ? `type-${typeChange.ticket.id}` : 'type-closed'}
          ticket={typeChange.ticket}
          ticketTypeOptions={ticketTypeOptions}
          paymentMethods={upgradePaymentMethods}
          currency={financials && financials.currency}
          submitting={typeChange.submitting}
          onClose={typeChange.close}
          onSubmit={typeChange.submit}
          onPreview={typeChange.preview}
          canRefund={canRefund}
        />
        <OrderGiftCardsCard giftCards={giftCards} onVoidOrRefund={giftCardAction.openFor} />
        <GiftCardVoidRefundModal
          key={giftCardAction.giftCard ? giftCardAction.giftCard.id : 'closed'}
          giftCard={giftCardAction.giftCard}
          submitting={giftCardAction.submitting}
          onClose={giftCardAction.close}
          onSubmit={giftCardAction.submit}
        />
        <OrderTransfersCard tickets={tickets} />
        <OrderCustomFieldsCard
          customFieldValues={customFieldValues}
          passAnswers={passAnswers}
          canEdit={capabilities.canUpdate}
          team={team}
          confirm={confirm}
          csrfToken={csrfToken}
          updateUrl={urls.update}
          defaultCountry={defaultCountry}
        />
        {attributionTimeline && <AttributionTimelineCard timeline={attributionTimeline} />}
        <OrderEmailsCard emails={emails} team={team} csrfToken={csrfToken} />
        <OrderNotesCard order={order} csrfToken={csrfToken} notesUrl={urls.notes} />
      </div>
    </div>
  );
};

OrderPage.propTypes = {
  order: PropTypes.object.isRequired,
  buyer: PropTypes.object,
  event: PropTypes.object,
  emails: PropTypes.array,
  tickets: PropTypes.array.isRequired,
  financials: PropTypes.object,
  capabilities: PropTypes.object.isRequired,
  printing: PropTypes.object,
  urls: PropTypes.object.isRequired,
  team: PropTypes.object,
  currentUser: PropTypes.object,
  confirm: PropTypes.object,
  csrfToken: PropTypes.string,
  refundReasons: PropTypes.array,
  venueOwedBalance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canBypassRefundRestrictions: PropTypes.bool,
  customFieldValues: PropTypes.array,
  passAnswers: PropTypes.array,
  defaultCountry: PropTypes.string,
  recipientCount: PropTypes.number,
  seating: PropTypes.shape({
    publicKey: PropTypes.string,
    eventKey: PropTypes.string.isRequired,
    ticketTypeNamesByCategory: PropTypes.objectOf(PropTypes.string).isRequired,
  }),
  giftCards: PropTypes.arrayOf(giftCardShape),
  ticketTypeOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  upgradePaymentMethods: PropTypes.arrayOf(PropTypes.string),
  upgradeLinks: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  })),
  attributionTimeline: PropTypes.object,
};

OrderPage.defaultProps = {
  buyer: null,
  event: null,
  emails: [],
  financials: null,
  printing: null,
  team: null,
  currentUser: null,
  confirm: null,
  csrfToken: null,
  refundReasons: [],
  venueOwedBalance: null,
  canBypassRefundRestrictions: false,
  customFieldValues: [],
  passAnswers: [],
  defaultCountry: null,
  recipientCount: 0,
  seating: null,
  giftCards: [],
  ticketTypeOptions: [],
  upgradePaymentMethods: [],
  upgradeLinks: [],
  attributionTimeline: null,
};

export default OrderPage;
